import React, { useState, createContext, useContext, useEffect } from "react";
import { useApiService } from "../shared/apiservice";
import { InboundDelivery } from "../shared/types";

export type SortOrder = "asc" | "desc";

const goodsInContext = createContext<{
    inboundDeliveries: InboundDelivery[];
    errors: string[];
    loading: boolean;
    page: number;
    perPage: number;
    total: number;
    searchTerm: string;
    sortField: string;
    sortOrder: SortOrder;
    fetchInboundDeliveries: () => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    setSearchTerm: (term: string) => void;
    setSort: (field: string) => void;
}>({
    inboundDeliveries: [],
    errors: [],
    loading: false,
    page: 1,
    perPage: 15,
    total: 0,
    searchTerm: "",
    sortField: "arrived_at",
    sortOrder: "desc",
    fetchInboundDeliveries: () => { },
    setPage: () => { },
    setPerPage: () => { },
    setSearchTerm: () => { },
    setSort: () => { },
});

function parseDelivery(d: any): InboundDelivery {
    return {
        ...d,
        started_at: new Date(d.started_at),
        arrived_at: new Date(d.arrived_at),
        completed_at: new Date(d.completed_at),
        pallets: (d.pallets ?? []).map((pallet: any) => ({
            ...pallet,
            items: (pallet.items ?? []).map((item: any) => ({
                ...item,
                expires_at: new Date(item.expires_at),
            })),
        })),
    };
}

function useProviderGoodsIn() {
    const [inboundDeliveries, setInboundDeliveries] = useState<InboundDelivery[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPageInternal] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTermInternal] = useState("");
    const [sortField, setSortField] = useState("arrived_at");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const apiService = useApiService();

    function setSearchTerm(term: string) {
        setSearchTermInternal(term);
        setPageInternal(1);
    }

    function setPage(p: number) {
        setPageInternal(p);
    }

    function setSort(field: string) {
        if (field === sortField) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
        setPageInternal(1);
    }

    useEffect(() => {
        fetchInboundDeliveries();
    }, [page, perPage, searchTerm, sortField, sortOrder]);

    async function fetchInboundDeliveries() {
        setLoading(true);
        setErrors([]);

        try {
            const params = new URLSearchParams({
                page: String(page),
                per_page: String(perPage),
                sort: sortField,
                order: sortOrder,
            });
            if (searchTerm.trim()) {
                params.set("q", searchTerm.trim());
            }
            const response = await apiService.get(
                `/deliveries/inbound?${params.toString()}`
            );
            if (!response.ok) {
                setErrors([
                    'Loading inbound deliveries failed. Please try again.',
                ]);
                return;
            }
            const json = await response.json();
            const data: InboundDelivery[] = (json.data ?? json).map(parseDelivery);
            setTotal(json.meta.total ?? data.length);
            setInboundDeliveries(data);
        } catch (error) {
            console.error(error);
            setErrors([
                'Loading inbound deliveries failed. Please try again.',
            ]);
        } finally {
            setLoading(false);
        }
    }

    return {
        inboundDeliveries,
        errors,
        loading,
        page,
        perPage,
        total,
        searchTerm,
        sortField,
        sortOrder,
        fetchInboundDeliveries,
        setPage,
        setPerPage,
        setSearchTerm,
        setSort,
    }
}

export function ProviderGoodsIn({ children }: { children: React.ReactNode }) {
    const goodsIn = useProviderGoodsIn();
    return <goodsInContext.Provider value={goodsIn}> {children}</goodsInContext.Provider>;
}

export const useGoodsIn = () => {
    return useContext(goodsInContext);
}

export function useGoodsInDetail(id: string | undefined) {
    const [delivery, setDelivery] = useState<InboundDelivery | null>(null);
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const apiService = useApiService();

    useEffect(() => {
        if (id) fetchDelivery();
    }, [id]);

    async function fetchDelivery() {
        setLoading(true);
        setErrors([]);

        try {
            const response = await apiService.get(`/deliveries/inbound/${id}`);
            if (!response.ok) {
                setErrors(['Loading delivery failed. Please try again.']);
                return;
            }
            const json = await response.json();
            setDelivery(parseDelivery(json.data ?? json));
        } catch (error) {
            console.error(error);
            setErrors(['Loading delivery failed. Please try again.']);
        } finally {
            setLoading(false);
        }
    }

    return { delivery, errors, loading, fetchDelivery };
}